import React, { memo, useRef, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { motion, useInView, useAnimation } from 'framer-motion'

import ROUTES from 'constants/routes'
import { CARD_STARS } from 'constants/stars'
import LottiePlayer from 'components/Shared/LottiePlayer'
import Star from 'components/Landing/Star'
import LandingButton from './LandingButton'

const ANIMATION_VARIANTS = {
  offscreen: 'offscreen',
  onscreen: 'onscreen'
}

const CARD_VARIANTS = {
  [ANIMATION_VARIANTS.offscreen]: {
    x: -200,
    opacity: 0
  },
  [ANIMATION_VARIANTS.onscreen]: {
    x: 0,
    opacity: 1,
    transition: {
      type: 'easeInOut',
      bounce: 0.4,
      duration: 1
    }
  }
}
const STARS_VARIANTS = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      type: 'easeInOut',
      duration: 2,
      delay: 0.2
    }
  }
}

function FeatureCard({ animationData, name, index }) {
  const { t } = useTranslation()
  const ref = useRef(null)

  const isInView = useInView(ref, { once: true, threshold: 1 })
  const controls = useAnimation()

  const [showStars, setShowStars] = useState(false)
  const [lottieRef, setLottieRef] = useState(null)

  useEffect(() => {
    if (isInView) {
      controls.start(ANIMATION_VARIANTS.onscreen)
    } else {
      controls.start(ANIMATION_VARIANTS.offscreen)
    }
  }, [isInView])

  function startLottie(variant) {
    if (lottieRef && variant === ANIMATION_VARIANTS.onscreen) {
      lottieRef.play()
      setShowStars(true)
    }
  }

  const title = t(`landing.features.${name}.title`)
  const featureList = t(`landing.features.${name}.list`, {
    returnObjects: true
  })

  return (
    <motion.div
      variants={CARD_VARIANTS}
      ref={ref}
      animate={controls}
      onAnimationComplete={startLottie}
    >
      <div className="feature-card">
        <div className="feature-card__container">
          <div className="feature-card__title">{title}</div>
          {showStars
            ? CARD_STARS.map((star) => (
                <motion.div initial="hidden" animate="visible" variants={STARS_VARIANTS}>
                  <Star
                    key={`feature-star-${star.position}`}
                    className={`feature__star feature__star--${star.position}${index + 1}`}
                    reverse={star.reverse}
                  />
                </motion.div>
              ))
            : ''}
          <LottiePlayer
            lottieRef={setLottieRef}
            animationData={animationData}
            className={`feature-card__animation feature-card__animation--${name}`}
            loop={false}
            autoplay={false}
          />
          <div className="feature-card__box">
            <ul className="feature-card__list">
              {featureList.map((feature) => (
                <li key={`feature-card-list-${feature}`} className="feature-card__list-item">
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          <LandingButton
            samePage
            to="landing-contact-us"
            text={t('landing.features.learn_more')}
            className="feature-card__button"
          />
        </div>
      </div>
    </motion.div>
  )
}

export default memo(FeatureCard)

FeatureCard.propTypes = {
  animationData: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired
}
